import { useEffect, useMemo } from 'react';
import {
  useLocation,
  useSearchParams,
  matchPath,
  generatePath,
} from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useResetClientAndApiState } from './useResetClientAndApiState';

import {
  clearItem,
  selectItem,
  setActiveGroupName,
  setCurrentViewMode,
  setListView,
  updateMapNetworkGroupId,
  updateSelectedAnalysis,
  updateSelectedNetworkGroup,
} from '@/features/analysisMapSlice';
import {
  ProjectFilteringViewModes,
  type ViewMode,
  ViewModeOptions as ViewModeOptionsConstants,
} from '@/features/types';
import { isInProjectUrl } from '@/helpers/url';
import {
  LISTS_BASE_PATH,
  LISTS_DETAIL_PATH,
  LISTS_SPAN_PATH,
  GROUP_PATH,
} from '@/app/constants';
import { useIsMapRoute } from '@/hooks/useIsMapRoute';

import { useSelector } from 'react-redux';
import type { RootState } from '@/app/store';
import {
  ANALYSIS_URL_PARAM,
  DEFAULT_ANALYSIS,
  applyAnalysisParam,
  parseAnalysisParam,
} from '@/hooks/useAnalysisParam';
import { useFetchNetworkGroupItemQuery } from '@/features/analysisApi';
import { useUpdateViewport } from './map/useUpdateViewport';
import { useGetListBboxQuery } from '@/features/customListSlice';
import { WKTBBoxToArray } from '@/helpers/stringConversion';
import { useSmallScreen } from '@/hooks/useWindowDimension';
import { clearQuery } from '@/features/filterSlice';

/**
 * Exported for testing purposes.
 * Don't use this directly - use the state from `mapLayerSlice` instead.
 */
export function getViewModeFromPath(pathname: string): ViewMode {
  const PATHS_TO_CHECK = [LISTS_BASE_PATH, LISTS_DETAIL_PATH, LISTS_SPAN_PATH];

  for (const path of PATHS_TO_CHECK) {
    const match = matchPath(path, pathname);

    if (!match) {
      continue;
    }

    switch (match.pattern.path) {
      case LISTS_BASE_PATH:
        return {
          mode: ViewModeOptionsConstants.CUSTOM_LIST_OVERVIEW,
          modeDetails: null,
        };
      case LISTS_DETAIL_PATH:
        return {
          mode: ViewModeOptionsConstants.CUSTOM_LIST_DETAIL,
          modeDetails: {
            id: match.params.id ?? '',
            name: match.params.name || '',
          },
        };
      case LISTS_SPAN_PATH:
        return {
          mode: ViewModeOptionsConstants.CUSTOM_LIST_SPAN,
          modeDetails: {
            id: match.params.id ?? '',
            name: match.params.name || '',
          },
        };
      default:
        break;
    }
  }

  return {
    mode: ViewModeOptionsConstants.LEGACY,
    modeDetails: null,
  };
}

/**
 * Returns the pathname of the logical root for the current route — what the user
 * should land on when dismissing an overlay or selection (e.g. a span page resolves
 * to its parent project). Search params are not handled here; pair with
 * `useNavigateWithSearch` to preserve them across the navigation.
 */
export function useCurrentRootPath() {
  const location = useLocation();

  return useMemo(() => {
    const routesRootMap: Record<string, string> = {
      [LISTS_SPAN_PATH]: LISTS_DETAIL_PATH,
      [LISTS_DETAIL_PATH]: LISTS_DETAIL_PATH,
      [LISTS_BASE_PATH]: LISTS_BASE_PATH,
      '/': '/',
    };

    for (const [route, rootRoute] of Object.entries(routesRootMap)) {
      const match = matchPath(route, location.pathname);
      if (match) {
        const params = Object.fromEntries(
          Object.entries(match.params).filter(([, v]) => v !== undefined)
        );
        return generatePath(rootRoute, params);
      }
    }

    return '/';
  }, [location.pathname]);
}

const useMapNetworkGroupIdSync = () => {
  const dispatch = useAppDispatch();
  const { currentViewMode, spanNetworkGroup, selectedNetworkGroup } =
    useAppSelector((s) => s.analysisMap);

  useEffect(() => {
    if (currentViewMode.mode === ViewModeOptionsConstants.CUSTOM_LIST_DETAIL) {
      dispatch(updateMapNetworkGroupId(spanNetworkGroup?.id ?? null));
    } else {
      dispatch(updateMapNetworkGroupId(selectedNetworkGroup?.id ?? null));
    }
  }, [
    dispatch,
    currentViewMode.mode,
    spanNetworkGroup?.id,
    selectedNetworkGroup?.id,
  ]);
};

export function useAnalysisTypeParamSync() {
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const isMapRoute = useIsMapRoute();
  const selectedAnalysis = useSelector(
    (state: RootState) => state.analysisMap.selectedAnalysis
  );
  // The objective is a map-view concern: the param never appears on non-map
  // URLs, Redux carries the value across them, and a map URL missing the param
  // is restored from Redux rather than treated as a reset. Writers always set
  // the param (including the default); once URL and Redux agree on the default
  // the param is redundant and gets cleaned up, which is what makes an absent
  // param unambiguous.
  useEffect(() => {
    if (!isMapRoute) return;

    if (!new URLSearchParams(location.search).has(ANALYSIS_URL_PARAM)) {
      if (selectedAnalysis !== DEFAULT_ANALYSIS) {
        setSearchParams((prev) => applyAnalysisParam(prev, selectedAnalysis), {
          replace: true,
        });
      }
      return;
    }
    const resolved = parseAnalysisParam(location.search);
    if (selectedAnalysis !== resolved) {
      dispatch(updateSelectedAnalysis(resolved));
      return;
    }
    if (resolved === DEFAULT_ANALYSIS) {
      setSearchParams((prev) => applyAnalysisParam(prev, resolved), {
        replace: true,
      });
    }
  }, [
    isMapRoute,
    location.search,
    selectedAnalysis,
    dispatch,
    setSearchParams,
  ]);
}

const useCustomListViewportSync = () => {
  const updateViewport = useUpdateViewport();
  const { currentViewMode } = useAppSelector((state) => state.analysisMap);
  const listId = currentViewMode.modeDetails?.id;

  const { data: listMeta, isLoading } = useGetListBboxQuery(
    { listId: listId ?? '' },
    { skip: !listId }
  );

  useEffect(() => {
    if (!listId || !listMeta || isLoading) {
      return;
    }

    const { bbox } = listMeta;

    if (bbox) {
      const bboxArray = WKTBBoxToArray(bbox);
      updateViewport(bboxArray);
    }
  }, [
    listId,
    listMeta,
    isLoading,
    currentViewMode.modeDetails?.id,
    updateViewport,
  ]);
};

export const useIsListViewSync = () => {
  const dispatch = useAppDispatch();
  const isSmallScreen = useSmallScreen();

  const { currentViewMode } = useAppSelector((state) => state.analysisMap);

  useEffect(() => {
    // Default us into "map view" when we open a list on mobile
    // Otherwise, put us into "list view" on desktop
    if (isInProjectUrl(currentViewMode)) {
      dispatch(setListView(!isSmallScreen));
    } else if (
      currentViewMode.mode === ViewModeOptionsConstants.CUSTOM_LIST_OVERVIEW
    ) {
      // There is no "map view" for the overview pane, so we always set it to list view on mobile
      dispatch(setListView(true));
    }
  }, [currentViewMode, dispatch, isSmallScreen]);
};

/** Hook for rendering page when in the /project/:id/:spanId url */
function useProjectActiveSpanSync() {
  const location = useLocation();
  const { spanNetworkGroup } = useAppSelector((s) => s.analysisMap);
  const dispatch = useAppDispatch();
  const updateViewport = useUpdateViewport();

  // Check if we're on a span-specific route and extract the span ID from the URL
  const spanMatch = matchPath(LISTS_SPAN_PATH, location.pathname);
  const { spanId } = spanMatch?.params || {};

  // Note: because the rest of the app does not yet require a level2
  // on a span identifier, we only utilize the level3 here.
  // In the future, it'll be safer to utilize both, given their
  // ~challenging~ uniqueness constraints.
  const decodedSpanId = spanId ? decodeURIComponent(spanId) : '';
  const [_level2, level3] = decodedSpanId?.split('~').map((s) => atob(s)) || [];

  const item = level3 || '';
  const { currentData: itemData } = useFetchNetworkGroupItemQuery(
    {
      item,
      group_id: spanNetworkGroup?.id,
    },
    {
      skip: !level3 || !spanNetworkGroup?.id,
    }
  );

  // Clear selection if no span in URL
  useEffect(() => {
    if (!spanId && spanMatch) {
      dispatch(clearItem());
    }
  }, [spanId, dispatch, spanMatch]);

  useEffect(() => {
    if (!itemData || !spanId) {
      return;
    }

    dispatch(selectItem({ object: itemData, layer: { id: 'scan' } }));

    if (itemData.bbox) {
      updateViewport(itemData.bbox, true);
    }
  }, [itemData, dispatch, spanId, updateViewport]);
}

/** Hook that renders the modal and bounding box of the span when in the /group/:id url */
export function useSelectedSpanSync() {
  const location = useLocation();
  const { selectedNetworkGroup } = useAppSelector((s) => s.analysisMap);
  const dispatch = useAppDispatch();
  const updateViewport = useUpdateViewport();

  const groupMatch = matchPath(GROUP_PATH, location.pathname);

  // Despite what the url would suggest, this points to a span ID, not a group ID
  // A url for groups has not been implemented yet
  const { id: spanId } = groupMatch?.params || {};

  const decodedSpanId = decodeURIComponent(spanId ?? '');
  const { currentData: itemData } = useFetchNetworkGroupItemQuery(
    {
      item: decodedSpanId,
      group_id: selectedNetworkGroup?.id,
    },
    {
      skip: !groupMatch || !decodedSpanId || !selectedNetworkGroup?.id,
    }
  );

  // Clear selection if no group in URL
  useEffect(() => {
    if (!spanId && groupMatch) {
      dispatch(clearItem());
    }
  }, [spanId, groupMatch, dispatch]);

  useEffect(() => {
    if (!itemData || !spanId) {
      return;
    }

    dispatch(selectItem({ object: itemData, layer: { id: 'scan' } }));

    if (itemData.bbox) {
      updateViewport(itemData.bbox, true);
    }
  }, [itemData, dispatch, spanId, updateViewport]);
}

export function isLeavingOrEnteringProject(
  currentViewMode: ViewModeOptionsConstants,
  nextMode: ViewModeOptionsConstants
): boolean {
  const enteringProjects =
    !ProjectFilteringViewModes.includes(currentViewMode) &&
    ProjectFilteringViewModes.includes(nextMode);

  const leavingProjects =
    ProjectFilteringViewModes.includes(currentViewMode) &&
    !ProjectFilteringViewModes.includes(nextMode);

  return enteringProjects || leavingProjects;
}

export function useViewModeSync() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const resetClientAndApiState = useResetClientAndApiState();

  const currentViewMode = useAppSelector(
    (state) => state.analysisMap.currentViewMode
  );

  useEffect(() => {
    const nextMode = getViewModeFromPath(location.pathname);
    if (currentViewMode.mode !== nextMode.mode) {
      dispatch(setCurrentViewMode(nextMode));
    }

    if (isLeavingOrEnteringProject(currentViewMode.mode, nextMode.mode)) {
      // Clear the filter query if we are entering or leaving a project
      dispatch(clearQuery());
      // Project and non-project views read different data, so drop the caches
      resetClientAndApiState();
    }
  }, [location.pathname, currentViewMode, dispatch, resetClientAndApiState]);
}

export function useUrlSync() {
  // Syncs the URL with the view mode, which is then referenced by the other hooks
  useViewModeSync();

  useAnalysisTypeParamSync();
  useProjectActiveSpanSync();
  useSelectedSpanSync();
  useMapNetworkGroupIdSync();
  useCustomListViewportSync();
  useCustomListNetworkGroupSync();
  useIsListViewSync();
}

const useCustomListNetworkGroupSync = () => {
  const location = useLocation();

  const dispatch = useAppDispatch();
  const { spanNetworkGroup, currentViewMode, activeGroupName } = useAppSelector(
    (s) => s.analysisMap
  );

  useEffect(() => {
    if (isInProjectUrl(currentViewMode)) {
      // Always showing "span mode" for custom list detail view
      dispatch(updateMapNetworkGroupId(spanNetworkGroup?.id ?? null));
      dispatch(updateSelectedNetworkGroup(spanNetworkGroup));
      // Setting the "activeGroupName" to null on custom list detail view as it does not render the geometry on mapview
      if (activeGroupName) {
        dispatch(setActiveGroupName(null));
      }
    }
  }, [currentViewMode, dispatch, location, spanNetworkGroup, activeGroupName]);
};
